/**
 * Pure helpers behind the Production Records DAY CARDS (PROD-1279 part two).
 *
 * The server's `/days` rollup says what is DONE on a campus-day (`transport`,
 * `service.records[].status`, `complete`); this module turns that into the
 * ONE card the timeline shows for it:
 *
 *   - PARTS are what the record is made of. The deliveries to receive are
 *     one part ("Deliveries", "1/2 received" while under way) and every
 *     mealtime on the day is at least one: a part per record where records
 *     exist ("Breakfast", "Lunch · Small Bites Catering" when two vendors
 *     share a mealtime), and ONE LOCKED part for a mealtime whose record the
 *     materializer has not generated yet. A locked part is counted but never
 *     done, so the entry count the card reads ("2 of 3 entries done") does
 *     not grow when that record materializes. The card does not draw the
 *     parts; it reads them as the count, the percent and the one action.
 *   - PERCENT is parts done over parts on the day. Each delivery counts once
 *     and each record counts once, so one truck and two mealtimes read
 *     33 / 67 / 100. A complete day is always 100; an open day never is.
 *   - STARTED (any part under way or done, or a locally saved draft) turns
 *     the card's one action from "Get started" into "Continue".
 *   - AWAITING RECORDS is the card's one disabled state: nothing on the day
 *     can be opened (no part to do or under way) and a record is still to be
 *     generated. While anything is openable the card keeps its percent and
 *     its action, locked mealtimes or not.
 *
 * A multi-campus school lists the same campus-day cards under a DAY header:
 * `groupCampusDays` builds those groups from the per-campus rollups the page
 * loads, `incompleteCampusDays` the red count on its Incomplete tab.
 *
 * Side-effect free; unit-tested in
 * `client/test/lib/production-records/day-progress.test.ts`.
 */

import {
  filterDays,
  type FilterState,
  type ProductionRecordDayRecordWire,
  type ProductionRecordDayWire,
} from '@/api/production-records'
import { formatRelativeDate, MEALTIME_LABEL } from '@/components/production/helpers'
import { isSignedStatus } from '@/components/production/record-insights'
import { compareMealtimes, type Mealtime } from '@/lib/mealtime'

export type DayPartState = 'done' | 'progress' | 'todo' | 'locked'

export interface DayPart {
  key: string
  /** "Deliveries", "Lunch", or "Lunch · Small Bites Catering" when two vendors share a mealtime. */
  label: string
  /** Progress inside the part ("1/2 received"); null when there is nothing to add. */
  detail: string | null
  state: DayPartState
}

export interface DayAction {
  label: 'Get started' | 'Continue'
  /** False while every open part is locked (records not generated yet). */
  enabled: boolean
}

export interface DayProgress {
  complete: boolean
  parts: DayPart[]
  done: number
  total: number
  /** 0..100; 100 exactly when the day is complete, never before. */
  percent: number
  started: boolean
  /** Nothing openable and a record still to be generated: dashed ring, no percent, disabled action. */
  awaitingRecords: boolean
  /** "Complete", "Not started", "In progress", "67% complete". */
  statusText: string
  /** "2 of 3 entries done", "Records not generated yet"; null on a complete day. */
  statusDetail: string | null
  /** The card's one action; null on a complete day (View details / Print take over). */
  action: DayAction | null
}

export interface DayDraftInput {
  /** Keyed by DELIVERY date: the receive wizard persists one draft per truck. */
  hasReceiveDraft: (deliveryDate: string) => boolean
  hasProductionDraft: boolean
}

export const NO_DRAFTS: DayDraftInput = { hasReceiveDraft: () => false, hasProductionDraft: false }

type DayInput = Pick<ProductionRecordDayWire, 'mealtimes' | 'transport' | 'service' | 'complete'>

/** Distinct mealtimes in time-of-day order (the wire is ordered already; this makes it a guarantee). */
function orderedMealtimes(mealtimes: ReadonlyArray<Mealtime>): Mealtime[] {
  return [...new Set(mealtimes)].sort(compareMealtimes)
}

/** Records in time-of-day order, vendors alphabetical within a mealtime. */
export function sortDayRecords(records: ReadonlyArray<ProductionRecordDayRecordWire>): ProductionRecordDayRecordWire[] {
  return [...records].sort(
    (a, b) => compareMealtimes(a.mealtime, b.mealtime) || (a.vendorName ?? '').localeCompare(b.vendorName ?? ''),
  )
}

/** "Lunch" or, when two vendors share a mealtime, "Lunch · Small Bites Catering". */
export function recordLabel(
  record: Pick<ProductionRecordDayRecordWire, 'mealtime' | 'vendorName'>,
  withVendor = false,
): string {
  const base = MEALTIME_LABEL[record.mealtime]
  return withVendor && record.vendorName ? `${base} · ${record.vendorName}` : base
}

/** A mealtime whose record the materializer has not generated yet: counted, never done, nothing to open. */
function lockedPart(mealtime: Mealtime): DayPart {
  return { key: `record:${mealtime}`, label: MEALTIME_LABEL[mealtime], detail: null, state: 'locked' }
}

/**
 * "2 of 3 entries done": the count behind the percent, on every open day (at
 * 0 too, so a fresh day says how much there is to do). Null only when the day
 * has nothing to count.
 */
export function entriesDoneLabel(done: number, total: number): string | null {
  if (total <= 0) {
    return null
  }
  return `${done} of ${total} ${total === 1 ? 'entry' : 'entries'} done`
}

export function dayProgress(day: DayInput, drafts: DayDraftInput = NO_DRAFTS): DayProgress {
  const parts: DayPart[] = []
  let done = 0
  let total = 0

  if (day.transport.status !== 'NONE') {
    const trucks = Math.max(day.transport.total, 1)
    const received = Math.min(Math.max(day.transport.received, 0), trucks)
    const drafting = day.transport.unreceivedDeliveryDates.some((date) => drafts.hasReceiveDraft(date))
    const state: DayPartState = received >= trucks ? 'done' : received > 0 || drafting ? 'progress' : 'todo'
    parts.push({
      key: 'deliveries',
      label: 'Deliveries',
      detail: state === 'progress' && trucks > 1 ? `${received}/${trucks} received` : null,
      state,
    })
    done += received
    total += trucks
  }

  if (day.service.recordsMissing) {
    // Nothing generated yet (the server sets this only when the day has no
    // record at all): every mealtime on the day is a locked part.
    const mealtimes = orderedMealtimes(day.mealtimes)
    if (mealtimes.length === 0) {
      parts.push({ key: 'record', label: 'Service records', detail: null, state: 'locked' })
      total += 1
    }
    for (const mealtime of mealtimes) {
      parts.push(lockedPart(mealtime))
      total += 1
    }
  } else if (day.service.status !== 'NONE') {
    const records = sortDayRecords(day.service.records)
    if (records.length === 0) {
      // Service work with no record rows behind it: one generic part carries
      // the rollup's own status, so the day still reads and still opens.
      const state: DayPartState =
        day.service.status === 'COMPLETE' ? 'done' : day.service.status === 'IN_PROGRESS' ? 'progress' : 'todo'
      parts.push({ key: 'record', label: 'Service records', detail: null, state })
      if (state === 'done') {
        done += 1
      }
      total += 1
    } else {
      // Every mealtime on the day (the server builds `mealtimes` from
      // shipments ∪ records) gets a part: the records where they exist, ONE
      // locked part where the materializer has not generated one yet. A day
      // with a Breakfast record and Lunch shipments only reads "of 3" before
      // AND after the Lunch record lands.
      const byMealtime = new Map<Mealtime, ProductionRecordDayRecordWire[]>()
      for (const record of records) {
        byMealtime.set(record.mealtime, [...(byMealtime.get(record.mealtime) ?? []), record])
      }
      for (const mealtime of orderedMealtimes([...day.mealtimes, ...byMealtime.keys()])) {
        const group = byMealtime.get(mealtime)
        if (!group) {
          parts.push(lockedPart(mealtime))
          total += 1
          continue
        }
        for (const record of group) {
          const signed = isSignedStatus(record.status)
          const state: DayPartState = signed ? 'done' : record.status === 'REVIEWED' ? 'progress' : 'todo'
          parts.push({ key: `record:${record.id}`, label: recordLabel(record, group.length > 1), detail: null, state })
          if (signed) {
            done += 1
          }
          total += 1
        }
      }
    }
  }

  const complete = day.complete
  if (complete) {
    done = total
  }
  // An open day never reads 100: 199 of 200 rounds to 100, and "100% complete"
  // beside a Continue button is a lie.
  const percent = complete ? 100 : total === 0 ? 0 : Math.min(99, Math.round((done / total) * 100))
  const started = complete || done > 0 || drafts.hasProductionDraft || parts.some((part) => part.state === 'progress')
  const openable = parts.some((part) => part.state === 'todo' || part.state === 'progress')
  // The one disabled state: nothing to open, and a record still to be
  // generated. A locked mealtime beside an openable one leaves the card live.
  const awaitingRecords = !complete && !openable && parts.some((part) => part.state === 'locked')

  const statusText = complete
    ? 'Complete'
    : percent > 0
      ? `${percent}% complete`
      : started
        ? 'In progress'
        : 'Not started'
  const statusDetail = complete ? null : awaitingRecords ? 'Records not generated yet' : entriesDoneLabel(done, total)
  const action: DayAction | null = complete ? null : { label: started ? 'Continue' : 'Get started', enabled: openable }

  return { complete, parts, done, total, percent, started, awaitingRecords, statusText, statusDetail, action }
}

/**
 * "Breakfast · Lunch": the day's mealtimes in time-of-day order. Reads the
 * records when there are any (the mealtime filter narrows those), else the
 * day's scheduled mealtimes.
 */
export function mealtimesLine(day: Pick<ProductionRecordDayWire, 'mealtimes' | 'service'>): string {
  const source = day.service.records.length > 0 ? day.service.records.map((record) => record.mealtime) : day.mealtimes
  return orderedMealtimes(source)
    .map((mealtime) => MEALTIME_LABEL[mealtime])
    .join(' · ')
}

function joinNames(names: ReadonlyArray<string>): string {
  if (names.length <= 1) {
    return names[0] ?? ''
  }
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`
}

/**
 * "Signed yesterday by Shannon Talmage". The signed name (typed at signing)
 * is the compliance signature; the account name is the fallback when the
 * signer left it blank. Several signers are named together. Null while no
 * record is signed.
 */
export function signedLine(day: Pick<ProductionRecordDayWire, 'service'>, now: Date = new Date()): string | null {
  const signed = day.service.records.filter(
    (record): record is ProductionRecordDayRecordWire & { submittedAt: string } =>
      isSignedStatus(record.status) && record.submittedAt !== null,
  )
  if (signed.length === 0) {
    return null
  }
  const latest = signed.reduce((a, b) => (a.submittedAt > b.submittedAt ? a : b))
  const names = [
    ...new Set(
      signed.map((record) => record.signedByName?.trim() || record.submittedByName?.trim() || '').filter(Boolean),
    ),
  ]
  const when = formatRelativeDate(latest.submittedAt, now)
  return names.length > 0 ? `Signed ${when} by ${joinNames(names)}` : `Signed ${when}`
}

/** The record "View details" opens: the day's first in time-of-day order; null when the day has none. */
export function firstRecordId(day: Pick<ProductionRecordDayWire, 'service'>): string | null {
  return sortDayRecords(day.service.records)[0]?.id ?? null
}

/** The one-day `/days` window for a yyyy-mm-dd service day. */
export function singleDayRange(serviceDate: string): { from: string; to: string } {
  return { from: `${serviceDate}T00:00:00.000Z`, to: `${serviceDate}T23:59:59.999Z` }
}

// ---------------------------------------------------------------------------
// Multi-campus grouping
// ---------------------------------------------------------------------------

export interface CampusRef {
  id: string
  name: string
}

export interface CampusDays {
  campus: CampusRef
  days: ReadonlyArray<ProductionRecordDayWire>
}

export interface CampusDayRow {
  campus: CampusRef
  day: ProductionRecordDayWire
}

export interface CampusDayGroup {
  /** yyyy-mm-dd */
  serviceDate: string
  /** The campus cards the current filters leave under this day, in campus order. */
  rows: CampusDayRow[]
  /** Every campus with service that day is complete (the status filter does not change this). */
  complete: boolean
  /** Campuses still open that day, the status filter aside. */
  incomplete: number
}

/** The campus filter: an empty set means every campus. */
export function selectedCampuses<T extends { campus: CampusRef }>(
  entries: ReadonlyArray<T>,
  campuses: ReadonlySet<string>,
): T[] {
  return entries.filter((entry) => campuses.size === 0 || campuses.has(entry.campus.id))
}

const NO_MEALTIMES: ReadonlySet<Mealtime> = new Set()

/**
 * Day groups for the multi-campus timeline, newest first. The status filter
 * decides which campus CARDS a day shows; the day header's rollup
 * (`complete`, `incomplete`) always describes every selected campus with
 * service that day, so "2 campuses incomplete" stays true while the
 * Incomplete tab is showing exactly those two.
 */
export function groupCampusDays(
  byCampus: ReadonlyArray<CampusDays>,
  state: Pick<FilterState, 'status' | 'mealtimes' | 'campuses'>,
): CampusDayGroup[] {
  const groups = new Map<string, CampusDayGroup>()
  for (const { campus, days } of selectedCampuses(byCampus, state.campuses)) {
    const inScope = filterDays(days, { status: 'all', mealtimes: state.mealtimes })
    const shown = new Set(
      filterDays(inScope, { status: state.status, mealtimes: NO_MEALTIMES }).map((day) => day.serviceDate),
    )
    for (const day of inScope) {
      let group = groups.get(day.serviceDate)
      if (!group) {
        group = { serviceDate: day.serviceDate, rows: [], complete: true, incomplete: 0 }
        groups.set(day.serviceDate, group)
      }
      if (!day.complete) {
        group.complete = false
        group.incomplete += 1
      }
      if (shown.has(day.serviceDate)) {
        group.rows.push({ campus, day })
      }
    }
  }
  return [...groups.values()]
    .filter((group) => group.rows.length > 0)
    .sort((a, b) => (a.serviceDate < b.serviceDate ? 1 : a.serviceDate > b.serviceDate ? -1 : 0))
}

/** The Incomplete tab's red count on a multi-campus school: open campus-days after the mealtime and campus filters. */
export function incompleteCampusDays(
  byCampus: ReadonlyArray<CampusDays>,
  state: Pick<FilterState, 'mealtimes' | 'campuses'>,
): number {
  return selectedCampuses(byCampus, state.campuses).reduce(
    (count, { days }) => count + filterDays(days, { status: 'pending', mealtimes: state.mealtimes }).length,
    0,
  )
}

/** The day header's rollup chip on a multi-campus school. */
export function dayGroupStatus(group: Pick<CampusDayGroup, 'complete' | 'incomplete'>): {
  tone: 'complete' | 'pending'
  label: string
} {
  if (group.complete) {
    return { tone: 'complete', label: 'All campuses complete' }
  }
  return {
    tone: 'pending',
    label: `${group.incomplete} ${group.incomplete === 1 ? 'campus' : 'campuses'} incomplete`,
  }
}
